import { Dialog, Transition } from '@headlessui/react';
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';
import { Dispatch, Fragment, SetStateAction, useEffect, useState } from 'react';
import { useMutation } from 'react-query';
import { Userpilot } from 'userpilot';
import { useAuth } from '../../../../../../scripts/fb';
import {
  useAccountHasPlanPaid,
  useAccountHasStarterPlan,
  useAccountHasTrialPlanPaid,
  useUpgradePlanToggle
} from '../../../../lib/store/dashboard.store';
import { useOpenUpUpgradeModal } from '../../../../lib/store/frame.store';
import {
  FreePrice,
  getWorkspace,
  isSafari,
  ProAnnualPrice,
  ProAnnualPriceTest,
  ProMonthlyPrice,
  ProMonthlyPriceTest,
  PublicKeyStripe,
  PublicKeyStripeTest,
  setLocalStorage,
  StarterAnnualPrice,
  StarterAnnualPriceTest,
  StarterMonthlyPrice,
  StarterMonthlyPriceTest
} from '../../../../utils';
import { PricingPage } from '../../../login/sign-up/pricing';
import { Button } from '../../button';
import { Switch } from '../../switch';
import { SwitchPlan } from './switch-plan';
import ReactHtmlParser from 'react-html-parser';

const domain = window.location.origin;
const stripePromise = loadStripe(
  domain.includes('uat') || domain.includes('localhost') ? PublicKeyStripeTest : PublicKeyStripe
);

export const FullyChecked = () => {
  return (
    <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]">
      <circle cx="12" cy="12" r="12" fill="#364152" />
      <path
        d="M7 12.5L10.5 16L17 9"
        stroke="white"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const HalfChecked = () => {
  return (
    <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]">
      <circle cx="12" cy="12" r="12" fill="#DAE0E9" />
      <path
        d="M7 12.5L10.5 16L17 9"
        stroke="white"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const PricingAnualMonthlyPage = ({
  trialExpired,
  closeIcon
}: {
  trialExpired: boolean;
  closeIcon: any;
}) => {
  const starterAccount = useAccountHasStarterPlan((state: any) => state.accountPaid);
  const accountPaid = useAccountHasPlanPaid((state: any) => state.accountPaid);
  const monthly = useUpgradePlanToggle((state: any) => state.checked);
  const trialAccountPaid = useAccountHasTrialPlanPaid((state: any) => state.trialAccountPaid);
  const auth = useAuth();

  useEffect(() => {}, [monthly]);

  const getSessionID = useMutation(async (payload: any) => {
    const token = await auth.user?.getIdToken();
    if (!token) return;
    const route = `/create_checkout_session`;
    try {
      const response = await axios.post(
        route,
        {
          domain: payload.domain,
          price_id: payload.priceId,
          trial_days: 0,
          success_url: '/new_file',
          cancel_url: '/new_file'
        },
        {
          headers: {
            Authorization: token
          }
        }
      );
      if (response && response.status === 200) {
        const stripe: any = await stripePromise;
        const { error } = await stripe.redirectToCheckout({
          sessionId: response.data.sessionId
        });
        if (error) {
          console.error('Stripe Checkout Error: ', error.message);
        }
      }
    } catch (error: any) {
      console.log(error);
    }
  });

  const handleCheckout = (from: string) => {
    const domain = window.location.origin;
    const isUat = domain.includes('uat') || domain.includes('localhost') ? true : false;
    if (!(isUat && from == 'free')) {
      const monthlyPro = isUat ? ProMonthlyPriceTest : ProMonthlyPrice;
      const yearPro = isUat ? ProAnnualPriceTest : ProAnnualPrice;
      const proPrice = monthly ? monthlyPro : yearPro;
      const monthlyStarter = isUat ? StarterMonthlyPriceTest : StarterMonthlyPrice;
      const yearStarter = isUat ? StarterAnnualPriceTest : StarterAnnualPrice;
      const starterPrice = monthly ? monthlyStarter : yearStarter;
      const payload = {
        priceId: from == 'free' ? FreePrice : from == 'starter' ? starterPrice : proPrice,
        domain
      };
      getSessionID.mutate(payload);
    }
  };

  const isFreeAccount = !accountPaid && !starterAccount && !trialAccountPaid;

  const plans = [
    {
      name: 'FREE',
      price: '$0',
      period: '/month',
      description:
        '<div class="flex justify-evenly"><span><b>1</b> file</span> <span><b>•</b></span><span><b>100</b> rows</span></div>',
      features: [
        { text: 'Data export', available: true },
        { text: 'File sharing', available: true },
        { text: '2x Data Connectors', available: false },
        { text: 'Enhanced AI ✨', available: false }
      ],
      buttonText: trialExpired ? 'Go FREE' : isFreeAccount ? 'Your plan' : 'Go FREE',
      highlight: '',
      index: 0
    },
    {
      name: 'STARTER',
      price: !monthly ? '$9' : '$15',
      period: '/month',
      description:
        '<div class="flex justify-evenly"><span><b>5</b> files</span> <span><b>•</b></span><span><b>1000</b> rows</span></div>',
      features: [
        { text: 'Data export', available: true },
        { text: 'File sharing', available: true },
        { text: '1x Data Connectors', available: !monthly ? true : false },
        { text: 'Enhanced AI ✨', available: false }
      ],
      buttonText: starterAccount ? 'Your plan' : 'Go STARTER',
      highlight: '<span class="text-[#000000] pl-1.5">Most Popular</span>',
      labelClass: 'bg-[#F4B400]',
      index: 1
    },
    {
      name: 'PRO',
      price: !monthly ? '$19' : '$25',
      period: '/month',
      description: '<b>Unlimited</b> files and rows',
      features: [
        { text: 'Data export', available: true },
        { text: 'File sharing', available: true },
        { text: '2x Data Connectors', available: !monthly ? true : false },
        { text: 'Enhanced AI ✨', available: true }
      ],
      buttonText: 'Go PRO',
      highlight: '<span class="text-[#FFFFFF] pl-1.5">Best Value</span>',
      labelClass: 'bg-[#E34653]',
      index: 2
    }
  ];

  const PricingCard = ({ plan }) => (
    <div className="border-solid border-[#BBBBBB] border-[1px] rounded-[10px] shadow-md px-6 py-2 text-center relative flex flex-col justify-between overflow-hidden">
      {plan.highlight && (
        <div className="absolute top-4 right-0">
          <div
            className={`${plan.labelClass} ${
              plan.index == 1 ? ' pl-9 pr-[27px]' : 'px-8'
            } translate-x-8 -translate-y-1 text-[10px] py-0.5  rotate-45 shadow-sm`}>
            {ReactHtmlParser(plan.highlight)}
          </div>
        </div>
      )}

      <h2 className="text-lg mb-2 text-start flex justify-start">{plan.name}</h2>
      <div className="text-[32px] font-bold text-[#1C8CA6] pt-3">
        {plan.price}
        <span className="pl-5 text-[#1C8CA6] text-[8px] font-semibold">{plan.period}</span>
      </div>
      <div className="mt-4 mb-4 border-solid border-[1px] border-[#000000] border-l-0 border-r-0">
        <span className="text-xs"> {ReactHtmlParser(plan.description)}</span>
      </div>

      <ul className="space-y-2.5 w-full">
        {plan.features.map((feature, index) => (
          <li
            key={index}
            className={`flex items-center gap-1 ${!feature.available ? 'text-[#C6C6C6]' : ''}`}>
            {feature.available ? (
              <>
                <FullyChecked />
              </>
            ) : (
              <>
                <HalfChecked />{' '}
              </>
            )}
            <span className="ml-2 text-sm">{feature.text}</span>
          </li>
        ))}
      </ul>
      <div className={`${plan.index == 0 && starterAccount ? 'invisible' : ''} pt-10 pb-2`}>
        <Button
          textColor={
            (plan.index == 0 && isFreeAccount && !trialExpired) ||
            (plan.index == 1 && starterAccount)
              ? '#000000'
              : '#FFFFFF'
          }
          description={plan.buttonText}
          backgroundColor={
            (plan.index == 0 && isFreeAccount && !trialExpired) ||
            (plan.index == 1 && starterAccount)
              ? '#fff'
              : '#1C8CA6'
          }
          fontSize={14}
          fontWeight={600}
          heigh={36}
          border={5}
          handleClick={() => {
            if ((plan.index == 0 && isFreeAccount) || (plan.index == 1 && starterAccount)) {
              closeIcon();
            } else if (plan.index == 1) {
              handleCheckout('starter');
            } else if (plan.index == 2) {
              handleCheckout('pro');
            }
          }}
          borderColor="#1C8CA6"
          className={`px-12 ${plan.buttonText == 'Your plan' ? 'cursor-default' : ''} `}
          borderSize="2px"
          full={false}
          removeDefaultSpaces={true}
        />
      </div>
    </div>
  );

  return (
    <div className="font-montserrat">
      <div className="flex justify-center pt-6 pb-6">
        {' '}
        <SwitchPlan />
      </div>

      <div className="flex justify-center w-full gap-8 pt-3">
        {plans.map((plan, index) => (
          <PricingCard key={index} plan={plan} />
        ))}
      </div>
    </div>
  );
};

export const UpgradePlanModal = ({
  openModal,
  setOpenModal,
  hasLogo,
  text,
  isUpgradeToAccess,
  setIsUpgradeToAccess,
  trialExpired
}: {
  openModal: boolean;
  setOpenModal?: Dispatch<SetStateAction<boolean>>;
  hasLogo: boolean;
  text: string;
  isUpgradeToAccess?: boolean;
  setIsUpgradeToAccess?: Dispatch<SetStateAction<boolean>>;
  trialExpired?: any;
}) => {
  const isSafariBrowser = isSafari();
  const setOpenUpgradeModal = useOpenUpUpgradeModal((state: any) => state.setOpen);
  const openUpgradeModal = useOpenUpUpgradeModal((state: any) => state.open);

  const closeIcon = () => {
    setOpenModal && setOpenModal(false);
    if (setIsUpgradeToAccess) setIsUpgradeToAccess(false);
    if (openUpgradeModal) setOpenUpgradeModal(false);
  };

  useEffect(() => {
    Userpilot.track('upgrade_button');
  }, []);

  return (
    <Transition.Root show={openModal} as={Fragment}>
      <Dialog as="div" className="relative z-[1000]" data-testid="modal-root" onClose={() => false}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0">
          <div className="fixed inset-0 bg-white/80 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto font-montserrat">
          <div
            className={`${
              window.outerWidth <= 820 || window.innerWidth <= 820
                ? !isSafariBrowser
                  ? ''
                  : ''
                : '2xl:-mt-[32px]'
            } ${
              isSafariBrowser && (window.outerWidth <= 820 || window.innerWidth <= 820)
                ? 'overflow-auto'
                : ''
            } flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0`}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
              <Dialog.Panel
                className={`${
                  window.outerWidth <= 820 || window.innerWidth <= 820
                    ? ''
                    : '2xl:w-[60%] w-[73%] overflow-hidden'
                } min-w-[1130px] max-w-[1100px] relative transform rounded-[25px] bg-white text-left transition-all shadow-[0_5px_30px_-1px_rgba(0,0,0,0.25)]`}>
                <div className="flex flex-col font-montserrat mx-12 pb-16">
                  <div className="flex flex-col text-center mt-2">
                    <div
                      className={`${
                        trialExpired ? 'pt-2' : 'pt-8'
                      } flex flex-row items-center justify-between pb-4`}>
                      <div className="flex justify-start flex-row items-center">
                        <img src="/img/logo-login.svg" alt="Logo" className="" />
                      </div>
                      <div className="flex justify-center items-center gap-2 mr-[6.5rem]">
                        {trialExpired ? (
                          <div className="flex flex-col pt-8">
                            <span className="text-sm">Your Trial period has expired. </span>
                            <span className="text-[#000000] font-semibold text-xl">
                              Please select a plan to continue:
                            </span>
                          </div>
                        ) : (
                          <>
                            <span className="text-[#000000] font-semibold text-xl">
                              Take it to the next level
                            </span>
                            <img src="/icons/rocket-colorful.svg" alt="Rocket" className="" />
                          </>
                        )}
                      </div>
                      <div className="cursor-pointer -mt-12 -mr-2 justify-end flex ">
                        <img
                          src="/icons/cancel.svg"
                          alt="Cancel icon"
                          className="h-4 mt-3"
                          onClick={closeIcon}
                        />
                      </div>
                    </div>

                    <div
                      className={`${
                        (text && text?.length > 0) || isUpgradeToAccess ? '' : ''
                      } w-full`}>
                      <PricingAnualMonthlyPage trialExpired={trialExpired} closeIcon={closeIcon} />
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
